import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useFocusScroll } from "@/lib/navigation";
import { api } from "@/lib/api";
import { useCustomerView } from "@/context/CustomerViewContext";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Bell, Loader2, ArrowRight } from "lucide-react";

export default function Notifications() {
  const [rows, setRows] = useState(null);
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const focusId = params.get("focus");  // V5-D NAV-002 exact notification
  const focusHit = !!(focusId && rows && rows.some((n) => n.id === focusId));
  useFocusScroll(focusHit ? `notification-${focusId}` : null, focusHit);
  const cv = useCustomerView(); // null under the admin shell (provider is dealer-scoped)
  const customerView = cv?.customerView ?? false;

  const load = () => {
    api.get(`/notifications${customerView ? "?customer_view=1" : ""}`).then((res) => setRows(res.data)).catch(() => setRows([]));
  };

  useEffect(load, [customerView]);

  const markRead = async (n) => {
    try {
      if (!n.read_at) {
        await api.post(`/notifications/${n.id}/read`);
        setRows((prev) => prev.map((x) => (x.id === n.id ? { ...x, read_at: new Date().toISOString() } : x)));
        window.dispatchEvent(new Event("notifications:changed"));
      }
      if (n.link) navigate(n.link);
    } catch {}
  };

  const markAllRead = async () => {
    try {
      await api.post("/notifications/read-all");
      setRows((prev) => prev.map((x) => ({ ...x, read_at: x.read_at || new Date().toISOString() })));
      window.dispatchEvent(new Event("notifications:changed"));
    } catch {}
  };

  return (
    <div data-testid="notifications-page">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-terracotta">Inbox</p>
          <h1 className="mt-3 font-display text-4xl font-light tracking-tight text-charcoal md:text-5xl">Notifications</h1>
        </div>
        {rows?.some((n) => !n.read_at) && (
          <Button variant="outline" onClick={markAllRead} data-testid="mark-all-read-button" className="border-limestone text-charcoal hover:bg-sand">
            Mark all read
          </Button>
        )}
      </div>

      {!rows ? (
        <Loader2 className="mt-12 h-6 w-6 animate-spin text-terracotta" />
      ) : rows.length === 0 ? (
        <Card className="mt-12 border-border bg-white">
          <CardContent className="flex flex-col items-center p-16 text-center">
            <Bell className="h-8 w-8 text-muted-foreground" />
            <p className="mt-4 text-sm text-muted-foreground" data-testid="notifications-empty">No notifications yet.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="mt-10 space-y-3">
          {rows.map((n) => (
            <Card
              key={n.id}
              id={`notification-${n.id}`}
              onClick={() => markRead(n)}
              data-focused={n.id === focusId ? "true" : undefined}
              className={`cursor-pointer border transition-colors duration-200 ${n.read_at ? "border-border bg-white opacity-75" : "border-terracotta/40 bg-white"} ${n.id === focusId ? "ring-2 ring-terracotta" : ""}`}
              data-testid={`notification-${n.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-3">
                  {!n.read_at && (
                    <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${n.kind === "alert" ? "bg-status-sold" : "bg-terracotta"}`} data-testid={`unread-dot-${n.id}`} />
                  )}
                  <div className="flex-1">
                    <div className="flex items-start justify-between gap-4">
                      <p className="text-sm font-medium text-charcoal">{n.title}</p>
                      <p className="whitespace-nowrap text-xs text-muted-foreground">{new Date(n.created_at).toLocaleString()}</p>
                    </div>
                    <p className="mt-1 text-sm text-warmgrey">{n.body}</p>
                    {n.actionable === false && (
                      <p className="mt-1.5 text-xs font-medium text-emerald-700" data-testid={`notification-resolved-${n.id}`}>
                        Resolved — no longer actionable{n.resolution ? ` (${n.resolution.reconciled} reconciled, ${n.resolution.excluded} excluded — not in inventory)` : ""}
                      </p>
                    )}
                    {n.link && (
                      <p className="mt-1.5 flex items-center gap-1 text-xs font-medium text-terracotta" data-testid={`notification-link-${n.id}`}>
                        Open <ArrowRight className="h-3 w-3" />
                      </p>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
